import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { ChartComponentProps, getBaseOption, getDisplayName, formatTooltipValue, ECHARTS_OPTS, isNumericLike, toNumericValue, getCategoryValue } from './chartConfig';

export const RadarChart: React.FC<ChartComponentProps> = ({ chart, height = 320, colors }) => {
  const option = useMemo((): EChartsOption => {
    const data = chart.data || [];
    if (data.length === 0) return {};

    const base = getBaseOption(colors);
    const fields = Array.from(new Set(data.flatMap((row) => Object.keys(row)))).filter((key) =>
      key !== 'name' && data.some((row) => isNumericLike(row[key]))
    );
    if (fields.length === 0) return {};

    const maxValues = fields.map((f) => Math.max(...data.map((d) => toNumericValue(d[f]))));

    const indicator = fields.map((f, i) => ({
      name: getDisplayName(chart, f),
      max: maxValues[i] * 1.2 || 100,
    }));

    const seriesData = data.map(d => ({
      name: getCategoryValue(d),
      value: fields.map((f) => toNumericValue(d[f])),
    }));

    return {
      ...base,
      radar: { indicator, shape: 'polygon' as const, splitNumber: 4 },
      tooltip: {
        ...(base.tooltip as object),
        trigger: 'item',
        formatter: (params: unknown) => {
          const p = params as { name?: string; value?: number[]; marker?: string };
          let html = `<div style="font-size:12px;color:#F7F3EE">${p.name}</div>`;
          fields.forEach((f, i) => {
            html += `<div style="font-size:12px">${getDisplayName(chart, f)}: ${formatTooltipValue(p.value?.[i])}</div>`;
          });
          return html;
        },
      },
      series: [
        {
          type: 'radar',
          data: seriesData,
          areaStyle: { opacity: 0.1 },
          emphasis: { areaStyle: { opacity: 0.25 } },
        },
      ],
    };
  }, [chart, colors]);

  return <ReactECharts option={option} style={{ height }} notMerge lazyUpdate opts={ECHARTS_OPTS} />;
};
